import React from 'react';
import { GitBranch, CheckCircle, ArrowRight, AlertTriangle, Users } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const RoundRobinDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Round Robin Assignment"
        description="Automatically distribute leads among your team members using intelligent assignment rules for fair workload distribution and faster response times."
        icon={<GitBranch className="w-6 h-6" />}
        badge="Automation"
        badgeColor="purple"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>✓ Understanding Round Robin assignment methods</li>
              <li>✓ Creating and configuring assignment groups</li>
              <li>✓ Setting up distribution rules by source and criteria</li>
              <li>✓ Managing team member availability and capacity</li>
              <li>✓ Monitoring assignment fairness and performance</li>
              <li>✓ Troubleshooting common assignment issues</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is Round Robin Assignment?</h2>
      <p>
        Round Robin is an automated lead distribution system that ensures new leads are assigned
        to team members fairly and efficiently. Instead of manually assigning each lead, the system
        automatically distributes them based on rules you configure.
      </p>

      <h3>Benefits of Round Robin</h3>
      <ul>
        <li><strong>Fair Distribution</strong> - Everyone gets equal opportunity to close deals</li>
        <li><strong>Faster Response</strong> - Leads assigned instantly as they arrive</li>
        <li><strong>No Manual Work</strong> - Eliminates time spent on manual assignment</li>
        <li><strong>Better Coverage</strong> - Ensures no lead goes unassigned</li>
        <li><strong>Load Balancing</strong> - Prevents any one person from being overwhelmed</li>
        <li><strong>Performance Tracking</strong> - Clear metrics on individual contribution</li>
      </ul>

      <h2>Assignment Methods</h2>

      <div className="not-prose space-y-4 my-6">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Sequential (Rotating)</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Leads are distributed in order, rotating through team members one by one.
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Example:</p>
            <p className="text-gray-600 dark:text-gray-400">
              Team: Alice, Bob, Charlie<br />
              Lead 1 → Alice<br />
              Lead 2 → Bob<br />
              Lead 3 → Charlie<br />
              Lead 4 → Alice (cycle repeats)
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            <strong>Best for:</strong> Teams where everyone has similar capacity and experience
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Load Balanced</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Leads assigned to the team member with the fewest active leads or lowest workload.
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Example:</p>
            <p className="text-gray-600 dark:text-gray-400">
              Alice: 5 active leads<br />
              Bob: 8 active leads<br />
              Charlie: 3 active leads<br />
              New lead → Charlie (has lowest count)
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            <strong>Best for:</strong> Teams with varying close rates or when members take time off
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Weighted Distribution</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Assign leads based on percentage weights. Senior members get more leads than juniors.
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Example:</p>
            <p className="text-gray-600 dark:text-gray-400">
              Alice (Senior): 50% weight<br />
              Bob (Mid): 30% weight<br />
              Charlie (Junior): 20% weight<br />
              Out of 10 leads: Alice gets 5, Bob gets 3, Charlie gets 2
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            <strong>Best for:</strong> Teams with different experience levels or capacity
          </p>
        </div>
      </div>

      <h2>Setting Up Round Robin Groups</h2>

      <h3>Step 1: Create a Group</h3>
      <ol>
        <li>Navigate to <code>Settings → Round Robin</code></li>
        <li>Click "Create New Group"</li>
        <li>Enter group name (e.g., "Sales Team", "Support Team", "Enterprise Team")</li>
        <li>Select assignment method (Sequential, Load Balanced, or Weighted)</li>
        <li>Click "Create Group"</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Create separate groups for different lead types or sources. For example: "Phone Leads Group",
          "Facebook Leads Group", "Website Leads Group" - each with specialized team members.
        </p>
      </div>

      <h3>Step 2: Add Team Members</h3>
      <ol>
        <li>Open the group you just created</li>
        <li>Click "Add Member"</li>
        <li>Select team member from dropdown</li>
        <li>If using weighted distribution, set their weight percentage</li>
        <li>Set availability status (Active/Inactive)</li>
        <li>Optionally set maximum leads per day limit</li>
        <li>Click "Add"</li>
        <li>Repeat for all team members in this group</li>
      </ol>

      <h3>Step 3: Configure Assignment Rules</h3>
      <p>Define when this group should receive leads:</p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">By Lead Source</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Assign leads based on where they came from:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Phone calls → Phone Sales Team</li>
            <li>• Facebook → Social Media Team</li>
            <li>• Website → Inbound Sales Team</li>
            <li>• WhatsApp → Support Team</li>
          </ul>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">By Time Window</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Distribute based on when lead arrives:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Business hours (9 AM - 6 PM) → Day Team</li>
            <li>• After hours → Night Team</li>
            <li>• Weekends → Weekend Team</li>
          </ul>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">By Lead Value</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Route high-value leads to senior team:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Value &gt; $50,000 → Senior Team</li>
            <li>• Value $10,000-50,000 → Mid Team</li>
            <li>• Value &lt; $10,000 → Junior Team</li>
          </ul>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">By Custom Field</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Route based on any custom criteria:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Product interest → Specialist Team</li>
            <li>• Location/Region → Regional Team</li>
            <li>• Language preference → Language Team</li>
          </ul>
        </div>
      </div>

      <h2>Managing Team Availability</h2>

      <h3>Individual Availability Settings</h3>
      <p>Each team member can have different availability states:</p>

      <div className="not-prose space-y-3 my-6">
        <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Active</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Receiving new lead assignments normally
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Limited Capacity</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Reached daily lead limit, won't receive more until tomorrow
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Inactive (On Leave/Busy)</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Temporarily not receiving assignments (vacation, training, etc.)
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex-shrink-0 mt-1">
            <div className="w-3 h-3 bg-gray-600 rounded-full"></div>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Removed from Group</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No longer part of this Round Robin group
            </p>
          </div>
        </div>
      </div>

      <h3>Setting Daily Limits</h3>
      <p>
        Protect team members from being overwhelmed by setting maximum leads per day:
      </p>
      <ul>
        <li>Go to group settings</li>
        <li>Click on team member's row</li>
        <li>Set "Max Leads Per Day" (e.g., 20)</li>
        <li>Once limit reached, member automatically marked as "Limited Capacity"</li>
        <li>Limit resets at midnight (configurable timezone)</li>
      </ul>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Important:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              If all team members reach their daily limit, new leads will queue and be assigned
              the next day. Consider setting up an overflow group or increasing limits during busy periods.
            </p>
          </div>
        </div>
      </div>

      <h2>Advanced Configuration</h2>

      <h3>Priority Reassignment</h3>
      <p>Automatically reassign leads if not contacted within a timeframe:</p>
      <ol>
        <li>Enable "Auto-Reassign" in group settings</li>
        <li>Set timeout period (e.g., 2 hours, 1 day)</li>
        <li>Choose reassignment behavior:
          <ul>
            <li><strong>Next in Rotation</strong> - Skip to next team member</li>
            <li><strong>Best Available</strong> - Assign to whoever has lowest load</li>
            <li><strong>Escalate to Manager</strong> - Assign to group supervisor</li>
          </ul>
        </li>
        <li>Original assignee gets notification of reassignment</li>
      </ol>

      <h3>Skill-Based Routing</h3>
      <p>Route leads to team members with specific skills or expertise:</p>
      <ul>
        <li>Tag team members with skills (e.g., "Spanish", "Enterprise Sales", "Technical")</li>
        <li>Create routing rules based on lead attributes</li>
        <li>Example: Spanish-speaking lead → Spanish-speaking team members only</li>
        <li>Falls back to general pool if no skilled member available</li>
      </ul>

      <h3>Territory-Based Assignment</h3>
      <p>Assign leads based on geographical location:</p>
      <ul>
        <li>Define territories (e.g., North Region, South Region)</li>
        <li>Assign team members to territories</li>
        <li>Leads auto-routed based on lead's location field</li>
        <li>Useful for businesses with regional sales teams</li>
      </ul>

      <h2>Monitoring & Analytics</h2>

      <h3>Distribution Dashboard</h3>
      <p>Track assignment fairness and team performance:</p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Leads Assigned</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Total leads assigned to each member (today, this week, this month)
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Distribution Balance</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Visual chart showing if distribution is fair across team
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Response Time</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Average time to first contact after assignment
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conversion Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Percentage of assigned leads converted to Won
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Active Load</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  Current number of active leads per team member
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h3>Assignment Audit Log</h3>
      <p>Every assignment is logged for transparency:</p>
      <ul>
        <li>Who was assigned the lead</li>
        <li>Which group/rule triggered the assignment</li>
        <li>Why they were chosen (rotation position, lowest load, etc.)</li>
        <li>Timestamp of assignment</li>
        <li>Any reassignments and reasons</li>
      </ul>

      <h2>Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Round Robin Success Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Set realistic limits</strong> - Don't overwhelm team members with too many leads</li>
              <li>✓ <strong>Update availability</strong> - Mark team members inactive when on leave</li>
              <li>✓ <strong>Monitor balance</strong> - Check distribution weekly to ensure fairness</li>
              <li>✓ <strong>Test before launch</strong> - Create test leads to verify rules work correctly</li>
              <li>✓ <strong>Review regularly</strong> - Adjust weights and rules based on performance</li>
              <li>✓ <strong>Have backup plans</strong> - Create overflow groups for high-volume periods</li>
              <li>✓ <strong>Communicate changes</strong> - Inform team when modifying assignment rules</li>
            </ul>
          </div>
        </div>
      </div>

      <h3>Common Scenarios & Solutions</h3>

      <div className="not-prose space-y-3 my-6">
        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Team member on vacation - what to do?
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2"><strong>Solution:</strong></p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to Round Robin settings</li>
              <li>Find the team member in their group(s)</li>
              <li>Click "Set Status" → "Inactive"</li>
              <li>Leads will skip them in rotation automatically</li>
              <li>Set status back to "Active" when they return</li>
            </ol>
            <p className="mt-2 text-xs italic">
              Their existing leads remain assigned to them. Only new assignments are affected.
            </p>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            New team member joins - how to onboard?
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2"><strong>Solution:</strong></p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Add them to the group with lower weight initially (e.g., 10%)</li>
              <li>Set lower daily limit (e.g., 5 leads/day) for first week</li>
              <li>Monitor their performance and response time</li>
              <li>Gradually increase weight and limit as they gain confidence</li>
              <li>Match team averages after training period (2-4 weeks)</li>
            </ol>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            Uneven distribution - some getting more than others?
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2"><strong>Possible causes & fixes:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Weights misconfigured:</strong> Check if weights add up correctly</li>
              <li><strong>Multiple groups:</strong> Member in several groups getting double assignments</li>
              <li><strong>Availability status:</strong> Some members marked inactive intermittently</li>
              <li><strong>Daily limits:</strong> Some hitting limits early, others getting overflow</li>
              <li><strong>Manual assignments:</strong> Bypassing Round Robin system</li>
            </ul>
            <p className="mt-2"><strong>Solution:</strong> Review distribution report and adjust settings accordingly</p>
          </div>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            High-value lead went to junior rep - can I change it?
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2"><strong>Yes, you can manually reassign:</strong></p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open the lead detail modal</li>
              <li>Click "Reassign" button in header</li>
              <li>Select senior team member</li>
              <li>Add reason for reassignment (logged in audit)</li>
              <li>Both team members get notification</li>
            </ol>
            <p className="mt-2 text-xs">
              <strong>Prevention:</strong> Set up value-based routing rules to prevent this in future
            </p>
          </div>
        </details>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Leads not being assigned automatically?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Round Robin group is "Active" (not paused)</li>
              <li>Assignment rules match the incoming lead source/criteria</li>
              <li>At least one team member is marked "Active" in the group</li>
              <li>No one has reached their daily limit</li>
              <li>Check assignment audit log for error messages</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Same person keeps getting all leads?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Possible causes:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Only one active member in the group (others inactive)</li>
              <li>Using Load Balanced method and one person closes leads faster</li>
              <li>Weight set to 100% for one person, 0% for others</li>
              <li>Other members hit daily limits</li>
            </ul>
            <p className="mt-2"><strong>Fix:</strong> Review member status and distribution settings</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can I manually assign a lead even with Round Robin active?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Yes!</strong> Manual assignments always override Round Robin:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Open lead detail modal</li>
              <li>Click "Assign To" dropdown</li>
              <li>Select any team member</li>
              <li>Lead is assigned, Round Robin counter is not affected</li>
              <li>Useful for special cases or VIP leads</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            How to temporarily pause Round Robin?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>To pause a group without deleting it:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Go to Round Robin settings</li>
              <li>Find the group you want to pause</li>
              <li>Click "Pause Group" toggle</li>
              <li>New leads won't be auto-assigned to this group</li>
              <li>Toggle back to "Active" to resume</li>
              <li>Existing assigned leads are not affected</li>
            </ol>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Master Your Pipeline</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Track assigned leads through your sales stages
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/user-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">User Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add and manage team members for Round Robin
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Automate Follow-ups</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up drip campaigns to nurture assigned leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Assignment Automation Ready!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              You've learned how to automate lead distribution with Round Robin. This ensures every
              team member gets fair opportunities and no lead falls through the cracks.
            </p>
            <a
              href="/settings/round-robin-groups"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Configure Round Robin
            </a>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RoundRobinDocs;
